import { useEffect, useMemo, useRef } from "react";

import { getUpgradeOptions } from "./useSubscriptionPlans/getUpgradeOptions";
import useSubscriptionStatus from "./useSubscriptionStatus";
import { resolveSubscriptionReactivation } from "./subscriptionUpgradeState";

const useLatest = value => {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
};

const useSubscriptionUpgradeController = ({
  visible,
  screen = "choose_plan",
  onOpen,
} = {}) => {
  const subscriptionStatus = useSubscriptionStatus();
  const reactivation = useMemo(
    () => resolveSubscriptionReactivation(subscriptionStatus),
    [subscriptionStatus]
  );
  const currentTier = subscriptionStatus.currentTier || "grandfathered";

  const upgradeOptions = useMemo(() => {
    if (!reactivation.isResolved) {
      return [];
    }

    return getUpgradeOptions(currentTier, {
      forReactivation: reactivation.isReactivation,
    });
  }, [currentTier, reactivation.isReactivation, reactivation.isResolved]);

  const isPending = reactivation.isPending;
  const isStatusUnavailable = reactivation.isUnavailable;
  const isAtChoosePlanScreen = screen === "choose_plan";
  const shouldShow = Boolean(visible && !isPending);

  const onOpenRef = useLatest(onOpen);
  const handledDecisionRef = useRef(null);

  useEffect(() => {
    if (!visible) {
      handledDecisionRef.current = null;
      return;
    }

    if (isPending) return;

    const decision = isStatusUnavailable ? "unavailable" : "open";

    // Uma mudança concorrente de status (ex.: outra assinatura resolvendo em paralelo) não
    // pode fechar o modal nem avisar o usuário depois que ele já avançou para checkout/success.
    if (decision !== "open" && !isAtChoosePlanScreen) return;

    if (handledDecisionRef.current === decision) return;
    handledDecisionRef.current = decision;

    if (decision === "open") {
      onOpenRef.current?.();
    }
  }, [isAtChoosePlanScreen, isPending, isStatusUnavailable, onOpenRef, visible]);

  return {
    currentTier,
    isPending,
    isReactivationFlow: reactivation.isReactivation,
    isStatusUnavailable,
    upgradeOptions,
    shouldShow,
  };
};

export default useSubscriptionUpgradeController;
